import type { ThirdPartyConfigsWithAPIResponse } from '@atlassian/rovo-connectors-config/schemas/types';

import type { LogoKey } from '../ProductLogos.tsx';

export type SuggestionProductKey =
	| 'googleDocs'
	| 'googleSheets'
	| 'googleSlides'
	| 'googleDrive'
	| 'loom'
	| 'microsoftTeams'
	| 'link';

export enum Action {
	SUGGEST = 'SUGGEST',
	ACCEPT = 'ACCEPT',
	REJECT = 'REJECT',
	IGNORE = 'IGNORE',
}

export const EXTERNAL_PRODUCT_CONNECTION = {
	LOOM: 'loom',
	GOOGLE_DRIVE: 'drive',
	MICROSOFT: 'microsoft',
} as const;

export type ExternalProductConnection =
	(typeof EXTERNAL_PRODUCT_CONNECTION)[keyof typeof EXTERNAL_PRODUCT_CONNECTION];

export type AuthenticationStatus = {
	isAuthenticated: boolean;
	outboundAuthUrl?: string;
	error?: Error;
	icon?: ThirdPartyConfigsWithAPIResponse['icon'];
};

export const ALL_EXTERNAL_PRODUCT_CONNECTIONS: ExternalProductConnection[] = Object.values(
	EXTERNAL_PRODUCT_CONNECTION,
);

export const SUGGESTION_SOURCE_BY_CONNECTION: Record<ExternalProductConnection, string> = {
	[EXTERNAL_PRODUCT_CONNECTION.LOOM]: 'loom',
	[EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE]: 'gdrive',
	[EXTERNAL_PRODUCT_CONNECTION.MICROSOFT]: 'teams',
};

export const AUTH_OPTIONS: readonly ['drive', 'microsoft'] = [
	EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE,
	EXTERNAL_PRODUCT_CONNECTION.MICROSOFT,
] as const;

export type SocialSignal = {
	totalVisits?: number;
	lastVisited?: string;
	ownerIsSpaceContributor?: boolean;
};

export type Suggestion = {
	id: string;
	title: string;
	description?: string;
	explanation?: string;
	socialSignal?: SocialSignal;
	relatedPages?: string[];
	url: string;
	parentId: number | null;
	action: Action;
	fallbackParents?: number[];
	iconUrl?: string;
	creatorAccountId?: string;
	providerId?: string;
	score?: number;
};

export type SuggestionGroup = {
	parentId: number | null;
	remoteObjects: Suggestion[];
};

export const PRODUCT_ICON_MAP: Array<{
	pattern: string;
	product: string;
	productKey: Exclude<SuggestionProductKey, 'link'>;
	logoKey: LogoKey;
}> = [
	{
		pattern: 'docs.google.com/document',
		product: 'Google Docs',
		productKey: 'googleDocs',
		logoKey: 'google-docs',
	},
	{
		pattern: 'docs.google.com/spreadsheets',
		product: 'Google Sheets',
		productKey: 'googleSheets',
		logoKey: 'google-sheets',
	},
	{
		pattern: 'docs.google.com/presentation',
		product: 'Google Slides',
		productKey: 'googleSlides',
		logoKey: 'google-slides',
	},
	{
		pattern: 'drive.google.com',
		product: 'Google Drive',
		productKey: 'googleDrive',
		logoKey: 'google-drive',
	},
	{
		pattern: 'loom.com',
		product: 'Loom',
		productKey: 'loom',
		logoKey: 'loom',
	},
	{
		pattern: 'teams.microsoft.com',
		product: 'Microsoft Teams',
		productKey: 'microsoftTeams',
		logoKey: 'microsoft-teams',
	},
	{
		pattern: 'sharepoint.com',
		product: 'Microsoft SharePoint',
		productKey: 'microsoftTeams',
		logoKey: 'microsoft-sharepoint',
	},
];

function getProductIconMapEntryForContentUrl(url: string) {
	return PRODUCT_ICON_MAP.find((entry) => url.includes(entry.pattern));
}

export function getSuggestionProductKeyFromContentUrl(url: string): SuggestionProductKey {
	return getProductIconMapEntryForContentUrl(url)?.productKey ?? 'link';
}

export function getSuggestionLogoKeyFromContentUrl(url: string): LogoKey | undefined {
	return getProductIconMapEntryForContentUrl(url)?.logoKey;
}
